/**
 * Fields of the builder's contact form (block "contact-form", settings.fields). Shared by the
 * storefront form and its editor, so both agree on what each field type is and how it is sent.
 *
 * Keys are chosen for the page translation heuristic (core/utils/translatable-fields.ts): `label`,
 * `placeholder`, `options` and `linkText` get translated with the page; `fieldType`, `width`,
 * `href` and `id` are skipped by key, so a translation can never change what a field means.
 */

export type ContactFieldType =
  | 'name' | 'email' | 'phone' | 'company' | 'nip' | 'country' | 'subject' | 'message'
  | 'text' | 'textarea' | 'select' | 'consent';

export interface ContactField {
  id: string;
  fieldType: ContactFieldType;
  /** Empty = the translated default for the type (contact.<type>_label). */
  label?: string;
  placeholder?: string;
  required?: boolean;
  /** 'half' = two side by side from the tablet breakpoint up. */
  width?: 'full' | 'half';
  /** Choices of a 'select' field. */
  options?: string[];
  /** Consent: the part of the label that links to `href` (e.g. "politykę prywatności"). */
  linkText?: string;
  href?: string;
}

interface FieldPreset {
  /** Name in the editor's "add field" list. */
  adminLabel: string;
  labelFallback: string;
  placeholderFallback?: string;
  input: 'input' | 'textarea' | 'select' | 'country' | 'checkbox';
  inputType?: string;
  autoComplete?: string;
  inputMode?: 'text' | 'email' | 'tel' | 'numeric';
  /** Only one of these makes sense per form (it fills a fixed part of the inquiry). */
  single?: boolean;
}

export const CONTACT_FIELD_PRESETS: Record<ContactFieldType, FieldPreset> = {
  name: { adminLabel: 'Imię i nazwisko', labelFallback: 'Imię i nazwisko', placeholderFallback: 'Jan Kowalski', input: 'input', autoComplete: 'name', single: true },
  email: { adminLabel: 'E-mail', labelFallback: 'Email', placeholderFallback: 'jan@example.pl', input: 'input', inputType: 'email', autoComplete: 'email', inputMode: 'email', single: true },
  phone: { adminLabel: 'Telefon', labelFallback: 'Telefon', placeholderFallback: '+48 600 000 000', input: 'input', inputType: 'tel', autoComplete: 'tel', inputMode: 'tel', single: true },
  company: { adminLabel: 'Firma', labelFallback: 'Firma', input: 'input', autoComplete: 'organization', single: true },
  nip: { adminLabel: 'NIP / VAT ID', labelFallback: 'NIP', input: 'input', single: true },
  country: { adminLabel: 'Kraj (lista)', labelFallback: 'Kraj', placeholderFallback: 'Wybierz kraj', input: 'country', autoComplete: 'country', single: true },
  subject: { adminLabel: 'Temat', labelFallback: 'Temat', input: 'input', single: true },
  message: { adminLabel: 'Wiadomość', labelFallback: 'Wiadomość', placeholderFallback: 'W czym możemy pomóc?', input: 'textarea', single: true },
  text: { adminLabel: 'Własne pole tekstowe', labelFallback: 'Pole', input: 'input' },
  textarea: { adminLabel: 'Własne pole wielowierszowe', labelFallback: 'Pole', input: 'textarea' },
  select: { adminLabel: 'Lista wyboru', labelFallback: 'Wybierz', placeholderFallback: 'Wybierz…', input: 'select' },
  consent: { adminLabel: 'Zgoda (pole wyboru)', labelFallback: 'Wyrażam zgodę na kontakt w sprawie mojego zapytania.', input: 'checkbox' },
};

/** The form as it always was — pages that never configured fields render exactly the same. */
export const DEFAULT_CONTACT_FIELDS: ContactField[] = [
  { id: 'name', fieldType: 'name', required: true },
  { id: 'email', fieldType: 'email', required: true },
  { id: 'message', fieldType: 'message', required: true },
];

/** Countries offered by the 'country' field; the name comes from Intl.DisplayNames in the page's language. */
export const CONTACT_COUNTRY_CODES = [
  'PL', 'DE', 'CZ', 'SK', 'LT', 'LV', 'EE', 'UA', 'AT', 'CH', 'NL', 'BE', 'LU', 'FR', 'ES', 'PT', 'IT',
  'GB', 'IE', 'DK', 'SE', 'NO', 'FI', 'IS', 'HU', 'RO', 'BG', 'GR', 'HR', 'SI', 'RS', 'CY', 'MT', 'TR',
  'US', 'CA', 'MX', 'BR', 'AR', 'AE', 'SA', 'IL', 'IN', 'CN', 'JP', 'KR', 'SG', 'AU', 'NZ', 'ZA',
];

export function contactFields(settings: { fields?: unknown }): ContactField[] {
  const fields = Array.isArray(settings.fields) ? (settings.fields as ContactField[]) : null;
  const valid = fields?.filter((field) => (
    field
    && typeof field === 'object'
    && typeof field.fieldType === 'string'
    && Object.hasOwn(CONTACT_FIELD_PRESETS, field.fieldType)
  ));
  if (!valid || valid.length === 0) return DEFAULT_CONTACT_FIELDS;

  // The submission endpoint uses the visitor's e-mail as Reply-To and validates it. Keep custom
  // forms usable even when an old/imported block omitted the field or marked it optional.
  const normalized = valid.map((field, index) => ({
    ...field,
    id: typeof field.id === 'string' && field.id ? field.id : `${field.fieldType}-${index + 1}`,
    ...(field.fieldType === 'email' ? { required: true } : {}),
  }));
  if (!normalized.some((field) => field.fieldType === 'email')) {
    normalized.splice(Math.min(1, normalized.length), 0, { id: 'email', fieldType: 'email', required: true });
  }
  return normalized;
}

export interface ContactPayload {
  form_type: 'contact';
  name: string;
  email: string;
  phone?: string;
  subject?: string;
  message: string;
  extra_data?: Record<string, string>;
}

const PAYLOAD_SLOT: Partial<Record<ContactFieldType, 'name' | 'email' | 'phone' | 'subject' | 'message'>> = {
  name: 'name', email: 'email', phone: 'phone', subject: 'subject', message: 'message',
};

/**
 * The inquiry the backend expects (/forms/submit) from the visitor's answers (`values` by field id):
 * the fixed fields fill their own slot, everything else goes to extra_data under the label the
 * visitor saw. `labelOf` resolves a field's displayed label; `countryName` a country code's name.
 */
export function buildContactPayload(
  fields: ContactField[],
  values: Record<string, string>,
  labelOf: (field: ContactField) => string,
  countryName: (code: string) => string,
): ContactPayload {
  const payload: ContactPayload = { form_type: 'contact', name: '', email: '', message: '' };
  const extra: Record<string, string> = {};
  const extraMessages: string[] = [];

  for (const field of fields) {
    const raw = (values[field.id] ?? '').trim();
    if (!raw) continue;
    const slot = PAYLOAD_SLOT[field.fieldType];
    if (slot && !payload[slot]) {
      payload[slot] = raw;
      continue;
    }
    const label = labelOf(field).trim() || field.fieldType;
    const value = field.fieldType === 'country' ? `${countryName(raw)} (${raw})`
      : field.fieldType === 'consent' ? '✓'
      : raw;
    if (field.fieldType === 'textarea') {
      extraMessages.push(`${label}:\n${value}`);
      continue;
    }
    let key = label;
    for (let n = 2; key in extra; n++) key = `${label} (${n})`;
    extra[key] = value;
  }

  if (extraMessages.length > 0) {
    payload.message = [payload.message, ...extraMessages].filter(Boolean).join('\n\n');
  }
  if (Object.keys(extra).length > 0) payload.extra_data = extra;
  return payload;
}
